import {createContext, useCallback, useEffect, useState} from "react";
import {useSelector} from "react-redux";

import {config} from "@/config.js";
import {selectUserPartnersAssigned} from "@/features/user/userSelectors";

export const AppVisibilityContext = createContext("");

/**
 * The INITIAL_APP_VISIBILITY is ready to extend when is required but currently
 * the only values the APP is using are:
 *
 * - healthScores
 * - resultsComponents.whatNext
 * - dashboardComponents.getMoreInsights
 * - dashboardComponents.referFriend
 * - dashboardComponents.learn
 * - signup
 */

const INITIAL_APP_VISIBILITY = {
  dashboardComponents: {
    improveYourHealth: true,
    preBookBanner: true,
    referFriend: true,
    getMoreInsights: true,
    blogs: true,
  },
  accountComponents: {
    customerHealthProfile: true,
    accountSettings: {changePassword: true, manageHealthCategories: true, thirdPartyAccess: true},
    subscriptions: {manageSubscriptions: true, reactiveSubscription: true},
    orderHistory: {
      getKitReplacement: true,
      changePhlebotomy: true,
      postedSample: true,
      cancelOrder: true,
      preBookManage: true,
    },
    creditAndVouchers: true,
    activateKit: true,
    referFriend: true,
    whatsNew: true,
    help: true,
    appRating: true,
    termCs: true,
    deleteAccount: true,
  },
  resultsComponents: {
    allResults: true,
    yourResults: true,
    healthScores: true,
    allMarkers: true,
    exportResults: true,
    whatNext: true,
  },
  plans: true,
  clinic: true,
  learn: true,
  healthScores: true,
  accountMenu: true,
  shop: true,
  signup: true,
};

const {websiteId} = config;

export const AppVisibilityProvider = ({children}) => {
  const [appVisibility, setAppVisibility] = useState(INITIAL_APP_VISIBILITY);

  const partnerAssigned = useSelector(selectUserPartnersAssigned);

  const isForth = websiteId === 1;
  const isEdge = websiteId === 2;

  const handleVisibility = useCallback(() => {
    if (isEdge) {
      setAppVisibility({
        ...INITIAL_APP_VISIBILITY,
        dashboardComponents: {
          ...INITIAL_APP_VISIBILITY.dashboardComponents,
          getMoreInsights: false,
        },
        healthScores: false,
      });
    }

    if (!isForth && !isEdge) {
      setAppVisibility({
        ...INITIAL_APP_VISIBILITY,
        dashboardComponents: {
          ...INITIAL_APP_VISIBILITY.dashboardComponents,
          improveYourHealth: false,
          getMoreInsights: false,
          preBookBanner: false,
          referFriend: false,
        },
        resultsComponents: {
          ...INITIAL_APP_VISIBILITY.resultsComponents,
          whatNext: false,
        },
        healthScores: partnerAssigned?.some(partner => partner.showHcReports) || false,
        shop: false,
        learn: false,
      });
    }
  }, [isForth, isEdge, partnerAssigned]);

  useEffect(() => {
    handleVisibility();
  }, [partnerAssigned]);

  return <AppVisibilityContext.Provider value={{appVisibility}}>{children}</AppVisibilityContext.Provider>;
};
